import { z } from 'zod';
import { timestampSchema } from './common';
import { scheduleTaskSchema, scheduleWarningSchema } from './scheduling';

/**
 * The rescheduling session and its notifications (requirements document
 * sections 5.4, 5.5 and 8.1-8.7; FR-030 to FR-043).
 *
 * The shape of the whole feature in one paragraph: edits made inside a session
 * are *proposals* held against the published schedule, never applied to it.
 * The server can run the engine with those proposals overlaid without writing
 * anything, which is what makes a live impact preview possible. On
 * confirmation everything lands in one transaction and each affected person
 * receives exactly one grouped notification -- which is the whole reason the
 * session exists, because notifying per edit is how a planning tool teaches
 * people to ignore it.
 */

export const scheduleChangeSessionStatusSchema = z.enum([
  /** Being edited. Nothing has been announced and nothing is applied. */
  'draft',
  /** Submitted, but it moves published work owned by other people. */
  'pending_approval',
  'applied',
  'cancelled',
  /**
   * A task the session proposes to change was edited by somebody else in the
   * meantime. The proposals survive; they have to be re-previewed against the
   * new base before they can be confirmed (section 8.7).
   */
  'conflicted',
]);
export type ScheduleChangeSessionStatus = z.infer<typeof scheduleChangeSessionStatusSchema>;

export const scheduleApprovalStatusSchema = z.enum(['pending', 'granted', 'declined']);
export type ScheduleApprovalStatus = z.infer<typeof scheduleApprovalStatusSchema>;

/** Why an impacted task matters to a person; see the impact module for the rules. */
export const scheduleMaterialChangeSchema = z.enum([
  'dates_moved',
  'duration_changed',
  'owner_changed',
  'became_unschedulable',
  'warning_introduced',
]);
export type ScheduleMaterialChange = z.infer<typeof scheduleMaterialChangeSchema>;

export const scheduleImpactOriginSchema = z.enum(['direct', 'propagated']);
export type ScheduleImpactOrigin = z.infer<typeof scheduleImpactOriginSchema>;

export const scheduleImpactSchema = z.object({
  taskId: z.string().uuid(),
  name: z.string(),
  /** Whether a person edited this task, or the engine moved it (section 8.3). */
  origin: scheduleImpactOriginSchema,
  ownerUserId: z.string().uuid().nullable(),
  contributorUserIds: z.array(z.string().uuid()),
  watcherUserIds: z.array(z.string().uuid()),
  /** Whether the task's owner has to agree before it moves. */
  isLocked: z.boolean(),

  previousStart: z.string().nullable(),
  previousFinish: z.string().nullable(),
  proposedStart: z.string().nullable(),
  proposedFinish: z.string().nullable(),
  /** Net movement in working days; positive is later. Null if either end is unscheduled. */
  shiftWorkingDays: z.number().int().nullable(),

  previousOwnerUserId: z.string().uuid().nullable(),
  materialChanges: z.array(scheduleMaterialChangeSchema),
  introducedWarnings: z.array(scheduleWarningSchema),
});
export type ScheduleImpact = z.infer<typeof scheduleImpactSchema>;

export const scheduleChangeApprovalSchema = z.object({
  approverUserId: z.string().uuid(),
  status: scheduleApprovalStatusSchema,
  comment: z.string().nullable(),
  decidedAt: timestampSchema.nullable(),
});
export type ScheduleChangeApproval = z.infer<typeof scheduleChangeApprovalSchema>;

export const scheduleChangeSessionSchema = z.object({
  id: z.string().uuid(),
  projectId: z.string().uuid(),
  status: scheduleChangeSessionStatusSchema,
  initiatedByUserId: z.string().uuid(),
  /** Required once the change affects other people (section 8.4). */
  note: z.string().nullable(),
  /** Tasks carrying a staged proposal, in the order they were staged. */
  changedTaskIds: z.array(z.string().uuid()),
  approvals: z.array(scheduleChangeApprovalSchema),
  createdAt: timestampSchema,
  updatedAt: timestampSchema,
  submittedAt: timestampSchema.nullable(),
  resolvedAt: timestampSchema.nullable(),
  version: z.number().int().nonnegative(),
});
export type ScheduleChangeSession = z.infer<typeof scheduleChangeSessionSchema>;

/** A task whose stored version moved on since the proposal was staged. */
export const scheduleChangeConflictSchema = z.object({
  taskId: z.string().uuid(),
  name: z.string(),
  stagedAgainstVersion: z.number().int().nonnegative(),
  currentVersion: z.number().int().nonnegative(),
});
export type ScheduleChangeConflict = z.infer<typeof scheduleChangeConflictSchema>;

/**
 * Everything the editing screen needs, from one call: the session, the
 * schedule as it *would* look, and who that would affect.
 *
 * `tasks` carries the projected schedule rather than the published one so the
 * Gantt, list and board can show the consequences of a proposal while it is
 * still a proposal -- section 8.1's "the UI should distinguish proposed dates
 * from currently published dates" needs both, and computing them twice on the
 * client from separate calls is how the two come to disagree.
 */
export const scheduleSessionPreviewSchema = z.object({
  session: scheduleChangeSessionSchema,
  tasks: z.array(scheduleTaskSchema),
  impacts: z.array(scheduleImpactSchema),
  /** Whose acceptance confirmation would require. Empty means confirm applies at once. */
  approverUserIds: z.array(z.string().uuid()),
  /** Everyone who would be notified, the initiator excluded. */
  notifyUserIds: z.array(z.string().uuid()),
  conflicts: z.array(scheduleChangeConflictSchema),
});
export type ScheduleSessionPreview = z.infer<typeof scheduleSessionPreviewSchema>;

export const submitScheduleSessionSchema = z
  .object({
    note: z.string().max(2000).nullish(),
    version: z.number().int().nonnegative(),
  })
  .strict();
export type SubmitScheduleSession = z.infer<typeof submitScheduleSessionSchema>;

export const decideScheduleSessionSchema = z
  .object({
    decision: z.enum(['granted', 'declined']),
    comment: z.string().max(2000).nullish(),
  })
  .strict();
export type DecideScheduleSession = z.infer<typeof decideScheduleSessionSchema>;

/* -------------------------------------------------------------------------- */
/* Notifications                                                              */
/* -------------------------------------------------------------------------- */

/**
 * Why a change reached this person, for one task. Ranked: somebody who owns a
 * task, is assigned to it and also watches it appears once, as its owner.
 */
export const scheduleTaskRoleSchema = z.enum(['owner', 'contributor', 'watcher']);
export type ScheduleTaskRole = z.infer<typeof scheduleTaskRoleSchema>;

export const scheduleNotificationKindSchema = z.enum([
  /** Tasks you own or watch have moved. */
  'impact',
  /** You own published work this change would move; it waits on your answer. */
  'approval_request',
  'approval_granted',
  'approval_declined',
  /** A change you were asked about has been applied. */
  'change_applied',
]);
export type ScheduleNotificationKind = z.infer<typeof scheduleNotificationKindSchema>;

/**
 * The before and after of one task, frozen into the notification (FR-042).
 *
 * Denormalized on purpose: a notification has to stay readable months later,
 * and re-reading the task would show wherever it has drifted to since rather
 * than what this change did. It is the same reason the audit trail stores an
 * actor label instead of a user id.
 */
export const scheduleNotificationTaskSchema = z.object({
  taskId: z.string().uuid(),
  name: z.string(),
  previousStart: z.string().nullable(),
  previousFinish: z.string().nullable(),
  proposedStart: z.string().nullable(),
  proposedFinish: z.string().nullable(),
  shiftWorkingDays: z.number().int().nullable(),
  origin: scheduleImpactOriginSchema,
  materialChanges: z.array(scheduleMaterialChangeSchema),
  /** The recipient's relationship to this task. */
  role: scheduleTaskRoleSchema,
});
export type ScheduleNotificationTask = z.infer<typeof scheduleNotificationTaskSchema>;

export const scheduleNotificationSchema = z.object({
  id: z.string().uuid(),
  kind: scheduleNotificationKindSchema,
  projectId: z.string().uuid(),
  /**
   * Kept alongside the id even though the mailbox no longer prints it: every
   * notification in a single-project deployment names the same project, so
   * showing it would repeat one word down the list. It stays on the wire
   * because a notification genuinely belongs to a project, and a reader of
   * several would have no other way to tell them apart.
   */
  projectName: z.string(),
  sessionId: z.string().uuid().nullable(),
  initiatorUserId: z.string().uuid().nullable(),
  /** Denormalized, so the message survives the actor being renamed or removed. */
  initiatorLabel: z.string(),
  note: z.string().nullable(),
  tasks: z.array(scheduleNotificationTaskSchema),
  /**
   * For an approval request, where that request stands *now* -- null for every
   * other kind, and for a request whose session has since gone away.
   *
   * The notification itself is a frozen record of what happened; whether it
   * still wants something from the reader is a live fact about the session,
   * and reading one from the other is what leaves an answered request sitting
   * in an inbox with its buttons still showing.
   */
  approvalStatus: scheduleApprovalStatusSchema.nullable(),
  /** The session's status, so a withdrawn request can say so. */
  sessionStatus: scheduleChangeSessionStatusSchema.nullable(),
  readAt: timestampSchema.nullable(),
  createdAt: timestampSchema,
});
export type ScheduleNotification = z.infer<typeof scheduleNotificationSchema>;

export const scheduleNotificationListQuerySchema = z.object({
  unreadOnly: z.coerce.boolean().optional(),
  cursor: z.string().max(256).optional(),
  limit: z.coerce.number().int().min(1).max(100).default(50),
});
export type ScheduleNotificationListQuery = z.infer<typeof scheduleNotificationListQuerySchema>;

/**
 * The inbox summary the shell polls: two counts, so a badge can be rendered
 * without pulling every notification into every page.
 */
export const scheduleInboxSummarySchema = z.object({
  unreadCount: z.number().int().nonnegative(),
  /** Requests waiting on this user's answer; these are the ones that block somebody. */
  pendingApprovalCount: z.number().int().nonnegative(),
});
export type ScheduleInboxSummary = z.infer<typeof scheduleInboxSummarySchema>;
